<template>
    <v-layout
        row
        wrap
    >
        <!-- TITLE OF THE PAGE-->
        <h2 class="custom_partner">
            {{ $t('administration.partner.search.mainTitle') }}
        </h2>

        <!-- FORM WITH RESEARCH BUTTON -->
        <v-layout
            row
            wrap
            justify-center
            class="layout_partner"
        >
            <v-flex
                xs5
                class="sizer_flex"
            >
                <v-text-field
                    v-model="filter.label"
                    dark
                    :label="$t('administration.partner.label.name')"
                    clearable
                    @keyup.enter="filterPartnersWithPaginationReset()"
                />
            </v-flex>
            <v-flex
                xs1
                justify-end
            >
                <v-btn
                    color="white"
                    small
                    fab
                >
                    <v-icon
                        color="primary"
                        dark
                        @keyup.enter="filterPartnersWithPaginationReset()"
                        @click="filterPartnersWithPaginationReset()"
                    >
                        search
                    </v-icon>
                </v-btn>
            </v-flex>
        </v-layout>

        <!-- PROGRESS BAR -->
        <v-layout
            v-if="displaySpinner"
            align-center
            justify-center
            row
        >
            <v-progress-circular
                :size="70"
                :indeterminate="true"
                color="primary"
            />
        </v-layout>

        <!-- DATA TABLES -->
        <v-flex v-if="!displaySpinner">
            <v-data-table
                :headers="headers"
                :items="partnerList"
                hide-actions
                disable-initial-sort
                align-center
                justify-center
                class="elevation-1"
            >
                <template slot="no-data">
                    <td
                        colspan="6"
                        class="text-xs-center"
                    >
                        {{ $t('administration.partner.search.none') }}
                    </td>
                </template>

                <template
                    slot="items"
                    slot-scope="props"
                >
                    <td class="align-items">
                        {{ props.item.label }}
                    </td>
                    <td class="align-items">
                        {{ props.item.code }}
                    </td>
                    <td class="align-items">
                        {{ props.item.importLabel }}
                    </td>
                    <td class="align-items">
                        {{ props.item.type }}
                    </td>
                    <td class="align-items">
                        <v-chip
                            v-for="m in props.item.markets"
                            :key="id"
                            :class="'t_entry_market_'+m.code"
                        >
                            {{ m.code }}
                        </v-chip>
                    </td>
                    <td class="align-items">
                        <router-link
                            tag="button"
                            :to="{name: 'PartnerEdit', params: { id: props.item.id }}"
                        >
                            <v-icon class="edit-icon">
                                edit
                            </v-icon>
                        </router-link>
                        <v-icon
                            slot="activator"
                            class="edit-icon"
                            @click="openConfirmationDialog(props.item.id)"
                        >
                            delete
                        </v-icon>
                    </td>
                </template>
            </v-data-table>

            <v-dialog
                v-model="dialog"
                persistent
                max-width="350"
                icon
            >
                <v-card>
                    <v-card-title class="headline">
                        {{ $t('administration.partner.manage.delete.title') }}
                    </v-card-title>
                    <v-divider />
                    <v-card-text>{{ $t('administration.partner.manage.delete.message') }}</v-card-text>
                    <v-divider />
                    <v-card-actions>
                        <v-spacer />
                        <v-btn
                            color="primary"
                            small
                            flat
                            @click.native="dialog = false"
                        >
                            {{ $t('administration.partner.manage.delete.cancel') }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            small
                            flat
                            @click.native="deletePartner()"
                        >
                            {{ $t('administration.partner.manage.delete.confirm') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-flex>

        <!-- PAGINATION -->
        <v-flex
            v-if="!!partnerList && partnerList.length > 0"
            xs12
            class="partner-list"
        >
            <v-pagination
                :length="pageable.totalPages"
                :total-visible="5"
                :value="pageable.page"
                circle
                @input="changePage"
            />
        </v-flex>

        <!-- ADD NEW PARTNER -->
        <v-tooltip top>
            <v-btn
                slot="activator"
                color="primary"
                small
                fab
                dark
                icon
                class="icon-add-custom"
                :to="{name: 'PartnerAdd'}"
            >
                <v-icon dark>
                    add
                </v-icon>
            </v-btn>
            <span>{{ $t('administration.partner.search.action.add') }}</span>
        </v-tooltip>
    </v-layout>
</template>

<script>

import axios from 'axios';
import EventBus from '../../../utils/event-bus.js';
import ConstantEvent from '../../../shared/constant/constantEvent.js';
import {mapState} from 'vuex';

export default {
    name:'Search',
    data () {
        return {
            partnerList: [],
            displaySpinner: false,
            pageable: {page: 1, size: 10,  direction: 'ASC', property: "label"},
            latestFilter: {},
            filter: {},
            dialog: false,
            headers:[
                {
                    text: this.$t('administration.partner.label.name'),
                    value:"label",
                    sortable: false,
                    align: 'center',
                },
                {
                    text: this.$t('administration.partner.label.abbreviation'),
                    value:"code",
                    sortable: false,
                    align: 'center',
                },
                {
                    text: this.$t('administration.partner.label.importLabel'),
                    value:"importLabel",
                    sortable: false,
                    align: 'center',
                },
                {
                    text: this.$t('administration.partner.label.type'),
                    value: 'type',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: this.$t('administration.partner.label.markets'),
                    value: "markets",
                    sortable: false,
                    align: 'center'
                },
                {
                    text: this.$t('administration.partner.label.actions'),
                    value:"actions",
                    sortable: false,
                    align: 'center',
                }
            ],
        }
    },
    created(){
        if (this.login) this.filterPartners();
    },
    computed: {
        ...mapState({
            login: state => state.userStore.userPreferences.login,
        }),
    },
    watch: {
        login (newValue, oldValue) {
            this.filterPartners();
        }
    },
    methods: {
        /**
             * Search partners by filter, reset page index to 1
             */
        filterPartnersWithPaginationReset() {
            this.pageable.page = 1;
            this.filterPartners();
        },
        /**
             * Search partners by filter
             */
        filterPartners() {
            this.latestFilter.label = this.filter.label;
            this.displaySpinner = true;
            axios.post(
                'administration/partner/filter',
                this.filter,
                {params: this.pageable}
            ).then( (result) => {
                this.displaySpinner = false;
                this.partnerList = result.data.content;
                this.pageable.totalElements = result.data.totalElements;
                this.pageable.totalPages = result.data.totalPages;
                this.pageable.page = result.data.page;
            });
        },
        /**
             * change page and navigate to the selected page
             * @param page
             */
        changePage(page) {
            this.pageable.page = page;
            this.filterPartners();
        },
        openConfirmationDialog(partnerId) {
            this.dialog = true;
            this.partnerIdToDelete = partnerId;
        },
        deletePartner() {
            this.dialog = false;
            axios.delete('administration/partner/' + this.partnerIdToDelete).then( () => {
                EventBus.$emit(ConstantEvent.ADD_NOTIFICATION, {
                    message: 'administration.partner.manage.delete.success',
                    type: 'SUCCEED'
                });
                if ( this.partnerList.length <= 1 && this.pageable.page > 1) {
                    this.pageable.page = this.pageable.page - 1;
                }
                this.filterPartners();
            });
        }
    }
}
</script>

<style scoped>
    .custom_partner{
        background-color: #355B7F;color:white;padding-top:25px;text-align: center;width:100vw;
    }
    .layout_partner{
        background-color: #355B7F;color:white;padding-top:10px;
    }
    .sizer_flex{
        width:100vw;
    }
    .align-items{
        text-align: center;
    }
    .icon-customer{
        margin-left: 15px;
    }
    .partner-list{
        transform: translate(45%,0);margin-top:15px;
    }
    .icon-add-custom{
        position: fixed; right: 50px; bottom: 0;
    }
</style>
